/* Boot fallback palette -- what renders before App.js's applyThemeVariant()
   writes the user's chosen theme onto <html>.
   These values MUST mirror the `keyflow` light theme in src/themeConfig.js,
   which is DEFAULT_LIGHT_THEME. They previously carried the retired navy /
   teal / orange identity, so every cold load flashed the *previous* brand for
   a frame before the theme engine caught up. Keep this block and its
   prefers-color-scheme counterpart below in sync with themeConfig.js. */
:root {
  --color-primary: #14513D;
  --color-secondary: #2A2A28;
  --color-dark: #0B1F18;
  --color-darkest: #0B0B0A;
  --color-light: #FAFAF6;
  --color-accent: #C49A4C;
  --color-warning: #C49A4C;
  --color-danger: #B85C3A;
  --financial-action-color: #14513D;
  --financial-action-color-hover: #1F6B52;

  --surface-primary: #FFFFFF;
  --surface-muted: #FAFAF6;
  --surface-subtle: #F2F0EA;
  --surface-strong: #E8E4D8;
  --surface-hover: #F2F0EA;
  --surface-info: rgba(20, 81, 61, 0.10);
  --surface-success: rgba(20, 81, 61, 0.10);
  --surface-danger: rgba(184, 92, 58, 0.12);
  --surface-danger-subtle: rgba(184, 92, 58, 0.06);
  --surface-warning: rgba(196, 154, 76, 0.16);
  --highlight-bg: #FCD34D;
  --highlight-text: #3B2A00;

  --body-bg: #FAFAF6;
  --text-primary: #0B1F18;
  --text-muted: #5E5C53;
  --border-color: rgba(11, 31, 24, 0.10);
  --gradient-header: linear-gradient(90deg, #0F3B2E 0%, #1B5C46 100%);
  --gradient-sidebar: linear-gradient(180deg, #0F3B2E 0%, #1B5C46 55%, #2A6B54 100%);
  --header-text-color: #F1EDD9;
  --header-chip-bg: rgba(196, 154, 76, 0.22);
  --header-chip-border: rgba(241, 237, 217, 0.30);
  --sidebar-text-color: #F1EDD9;
  --sidebar-item-hover: rgba(241, 237, 217, 0.10);
  --sidebar-item-active: rgba(196, 154, 76, 0.22);
  --sidebar-border-color: rgba(241, 237, 217, 0.12);
  --sidebar-active-border: rgba(196, 154, 76, 0.55);
  --sidebar-active-shadow: 0 18px 36px rgba(11, 31, 24, 0.40), 0 0 0 1px rgba(196, 154, 76, 0.30);
  --action-link-color: var(--color-primary);
  --badge-buyer-bg: rgba(20, 81, 61, 0.20);
  --badge-buyer-text: #14513D;
  --badge-seller-bg: rgba(196, 154, 76, 0.24);
  --badge-seller-text: #6B4F1A;
  --badge-self-bg: rgba(20, 81, 61, 0.16);
  --badge-self-text: #14513D;
  --pill-warn-text: #6B4F1A;
  --shadow-card: 0 1px 2px rgba(11,31,24,0.04), 0 6px 18px -10px rgba(11,31,24,0.10);
  --shadow-lift: 0 2px 6px rgba(11,31,24,0.06), 0 18px 40px -22px rgba(11,31,24,0.28);
}

/* Theming hardening (Goal #2): tokens referenced in src/ that were never
   defined. Colors compose from theme tokens via color-mix so they adapt to the
   active theme; fonts/radius/easing are theme-agnostic constants. */
:root {
  /* Text */
  --text-secondary: color-mix(in srgb, var(--text-primary), var(--text-muted));
  --text-faint: color-mix(in srgb, var(--text-muted), var(--surface-primary) 45%);

  /* Surfaces / borders */
  --border-strong: color-mix(in srgb, var(--border-color), var(--text-muted) 35%);

  /* Status */
  --color-success: var(--financial-action-color);

  /* Transaction status pipeline */
  --pipeline-active: var(--color-accent);
  --pipeline-active-hover: color-mix(in srgb, var(--color-accent), black 12%);
  --pipeline-done: var(--financial-action-color);
  --pipeline-done-hover: color-mix(in srgb, var(--financial-action-color), black 12%);
  --pipeline-text-active: var(--on-accent);
  --pipeline-text-done: #ffffff;

  /* Referral badge (mirrors the existing --badge-* pairs) */
  --badge-referral-bg: rgba(124, 58, 237, 0.18);
  --badge-referral-text: #4c1d95;

  /* Static design constants.
     --font-sans / --font-mono must stay in sync with the families actually
     loaded by index.html and declared in tailwind.config.js. Nothing else in
     this file may name a family literally — a face that isn't loaded falls
     back silently, which is exactly how 'Lexend' shipped unnoticed. */
  --font-sans: 'Geist', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-display: var(--font-sans);
  --font-mono: 'Geist Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, 'Cascadia Code', monospace;
  --radius-card: 12px;
  --ease-rise: cubic-bezier(0.2, 0.7, 0.2, 1);
}

/* Dark boot fallback. Mirrors the `keyflowDark` theme in src/themeConfig.js,
   which is DEFAULT_DARK_THEME. Same contract as the light block above. */
@media (prefers-color-scheme: dark) {
  :root {
    --color-primary: #3FA37F;
    --color-secondary: #9C9A92;
    --color-dark: #E8E4D8;
    --color-darkest: #FAFAF6;
    --color-light: #0B0B0A;
    --color-accent: #D4A953;
    --color-warning: #D4A953;
    --color-danger: #D17A57;
    --financial-action-color: #5BC29F;
    --financial-action-color-hover: #3FA37F;

    --surface-primary: #161614;
    --surface-muted: #0B0B0A;
    --surface-subtle: #1A1A18;
    --surface-strong: #2A2A28;
    --surface-hover: #1F1F1D;
    --surface-info: rgba(63, 163, 127, 0.18);
    --surface-success: rgba(63, 163, 127, 0.18);
    --surface-danger: rgba(209, 122, 87, 0.20);
    --surface-danger-subtle: rgba(209, 122, 87, 0.10);
    --surface-warning: rgba(200, 169, 95, 0.20);
    --highlight-bg: #8A5A00;
    --highlight-text: #FFF8E1;

    --body-bg: #0B0B0A;
    --text-primary: #FAFAF6;
    --text-muted: #9C9A92;
    --border-color: rgba(250, 250, 246, 0.08);
    --gradient-header: #161614;
    --gradient-sidebar: #0B0B0A;
    --header-text-color: #FAFAF6;
    --header-chip-bg: rgba(63, 163, 127, 0.14);
    --header-chip-border: rgba(63, 163, 127, 0.28);
    --sidebar-text-color: #E8E4D8;
    --sidebar-item-hover: rgba(250, 250, 246, 0.06);
    --sidebar-item-active: rgba(63, 163, 127, 0.28);
    --sidebar-border-color: rgba(250, 250, 246, 0.08);
    --sidebar-active-border: rgba(91, 194, 159, 0.80);
    --sidebar-active-shadow: 0 20px 38px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(91, 194, 159, 0.55);
    --action-link-color: #5BC29F;
    --badge-buyer-bg: rgba(63, 163, 127, 0.22);
    --badge-buyer-text: #C8E8DA;
    --badge-seller-bg: rgba(200, 169, 95, 0.22);
    --badge-seller-text: #E8D9B5;
    --badge-self-bg: rgba(63, 163, 127, 0.20);
    --badge-self-text: #C8E8DA;
    --pill-warn-text: #fbd38d;
    --shadow-card: 0 1px 2px rgba(0,0,0,0.3), 0 8px 24px -12px rgba(0,0,0,0.6);
    --shadow-lift: 0 2px 8px rgba(0,0,0,0.4), 0 22px 48px -22px rgba(0,0,0,0.7);
  }
}

html,
body,
#root {
  height: 100%;
}

html {
  font-size: 15px;
}

@media (max-width: 1440px) {
  html {
    font-size: 14.5px;
  }
}

@media (max-width: 1280px) {
  html {
    font-size: 14px;
  }
}

body {
  margin: 0;
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--body-bg);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
  overflow: hidden;
}

.app-shell {
  background: var(--body-bg);
  color: var(--text-primary);
}

.transaction-action--financials {
  color: var(--financial-action-color);
  transition: color 0.2s ease;
}

.transaction-action--financials:hover {
  color: var(--financial-action-color-hover);
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.3rem 0.7rem;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  background-color: var(--surface-subtle);
  color: var(--text-primary);
  box-shadow: 0 2px 8px rgba(11, 31, 24, 0.12);
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.badge--buyer {
  background-color: var(--badge-buyer-bg);
  color: var(--badge-buyer-text);
}

.badge--seller {
  background-color: var(--badge-seller-bg);
  color: var(--badge-seller-text);
}

.badge--referral {
  background-color: var(--badge-referral-bg);
  color: var(--badge-referral-text);
}

/* Rental (Leasing/Rental) transaction type. Rose/pink — deliberately outside
   the buyer (blue), seller (green), and referral (purple) hues so the type is
   never confused with them. Text falls back to the theme foreground so it stays
   legible in every theme (light and dark) without a per-theme token. */
.badge--rental {
  background-color: var(--badge-rental-bg, rgba(236, 72, 153, 0.16));
  color: var(--badge-rental-text, var(--text-primary));
}

.badge--self {
  background-color: var(--badge-self-bg);
  color: var(--badge-self-text);
  box-shadow: 0 0 0 1px rgba(11, 31, 24, 0.05);
}

.sidebar {
  position: relative;
  background: var(--gradient-sidebar, var(--surface-primary));
  color: var(--sidebar-text-color, var(--text-primary));
  border-right: 1px solid var(--sidebar-border-color, var(--border-color));
}

/* Reset native button chrome for sidebar buttons — but NOT the floating
   collapse toggle, which intentionally has its own filled background. */
.sidebar button:not(.sidebar__toggle) {
  background: transparent;
  border: none;
}

.sidebar__top {
  /* Padding-left chosen so the 32px logo's CENTER lands at 38px from
     sidebar-left (sidebar-center in the 76px collapsed state) — left
     padding 1.375rem = 22px, +16 (half of 32) = 38 center. Matches
     the avatar's center column (avatar 28px, left at 24, center at 38). */
  padding: 1rem 0.875rem 0.75rem 1.375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sidebar__brand-mark {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  flex: 1;
  color: var(--sidebar-text-color);
}

.sidebar__logo {
  /* 32x32 outer with 2px padding gives a 28px visible glyph — matches the
     workspace avatar's filled gold area exactly so the two squircles read
     as the same visual weight in the same column. */
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  /* 10px, not 9px: the avatar inside is 28px at an 8px radius, and concentric
     corners want outer = inner + gap (8 + 2px padding). */
  border-radius: 10px;
  object-fit: contain;
  background: rgba(241, 237, 217, 0.10);
  padding: 2px;
  box-shadow: 0 0 0 1px rgba(241, 237, 217, 0.14);
}

/* Text labels — always rendered in DOM. When the sidebar collapses, the
   parent `.sidebar--collapsed` fades these out (opacity) and they get clipped
   from the right by the narrowing parent width. One coordinated motion
   instead of an instant DOM removal followed by a width transition. */
.sidebar__brand,
.sidebar__item-label {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--font-sans);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 1;
  transition: opacity 0.18s ease-out;
}

.sidebar__brand {
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--sidebar-text-color);
}

.sidebar--collapsed .sidebar__brand,
.sidebar--collapsed .sidebar__item-label {
  opacity: 0;
  pointer-events: none;
  /* Take zero layout space too — without this, the flex 'gap' between icon
     and label still occupies ~12px to the right of the icon, making the
     icon visually left-of-center within the highlight rectangle (the user
     sees this as "extra white space on the right" of the icon). */
  flex: 0 0 0;
  margin: 0;
  transition: opacity 0.12s ease-in, flex-basis 0.25s ease, margin 0.25s ease;
}

/* Collapse the flex 'gap' too so no whitespace sits between the icon and
   the zero-width label — icon ends up perfectly centered in the highlight. */
.sidebar--collapsed .sidebar__item,
.sidebar--collapsed .sidebar__brand-mark {
  gap: 0;
}

.sidebar__workspace {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0;
    padding: 10px 12px;
    margin: 0 12px 8px;
    border-radius: 10px;
    background: rgba(241, 237, 217, 0.08);
    /* The visible cream outline is a box-shadow ring rather than a border,
       so it does not consume the box-sizing:border-box content area. This
       lets the 28px avatar sit at the padding edge with truly symmetric
       padding on both sides instead of overflowing the content area to
       the right (which made it visually 2px left-of-center). */
    box-shadow: 0 0 0 1px rgba(241, 237, 217, 0.18);
    color: var(--sidebar-text-color);
    cursor: pointer;
    transition: background .15s ease, box-shadow .15s ease, margin .25s ease;
}
.sidebar__workspace:hover,
.sidebar__workspace:focus-within {
    background: rgba(241, 237, 217, 0.14);
    box-shadow: 0 0 0 1px rgba(241, 237, 217, 0.26);
    outline: none;
}
.sidebar__workspace--collapsed {
    /* Keep vertical padding identical to the expanded state so the tile's
       height doesn't change on collapse (no vertical pop). Only horizontal
       margin/padding tighten. Margin chosen so the 28px avatar centers in
       the 76px collapsed sidebar (avatar-left at 14px from sidebar-left). */
    padding: 10px 10px;
    margin: 0 14px 8px;
    gap: 0;
}
.sidebar__workspace-avatar {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    background: var(--color-accent);
    color: var(--color-dark);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    overflow: hidden;
}
/* When the workspace has an uploaded logo, switch the avatar to the same
   chip-style backdrop the KeyFlow logo uses so the two icons feel like
   members of the same family. */
.sidebar__workspace-avatar--has-logo {
    background: rgba(241, 237, 217, 0.10);
    padding: 2px;
    box-shadow: 0 0 0 1px rgba(241, 237, 217, 0.14);
}
.sidebar__workspace-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
    display: block;
}
.sidebar__workspace-name {
    flex: 1 1 auto;
    min-width: 0;
    margin-left: 10px;
    font-size: 13px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 1;
    transition: opacity 0.18s ease-out, flex-basis 0.25s ease, margin-left 0.25s ease;
}
.sidebar__workspace-chevron {
    flex: 0 0 14px;
    width: 14px;
    height: 14px;
    margin-left: 10px;
    opacity: 0.7;
    overflow: hidden;
    transition: opacity 0.18s ease-out, flex-basis 0.25s ease, width 0.25s ease, margin-left 0.25s ease;
}
.sidebar__workspace--collapsed .sidebar__workspace-name {
    opacity: 0;
    pointer-events: none;
    flex: 0 0 0;
    margin-left: 0;
    transition: opacity 0.12s ease-in, flex-basis 0.25s ease, margin-left 0.25s ease;
}
.sidebar__workspace--collapsed .sidebar__workspace-chevron {
    opacity: 0;
    pointer-events: none;
    flex-basis: 0;
    width: 0;
    margin-left: 0;
    transition: opacity 0.12s ease-in, flex-basis 0.25s ease, width 0.25s ease, margin-left 0.25s ease;
}

/* Sidebar collapse/expand toggle — floating circular tab on the right edge of
   the sidebar (seam between sidebar and main content). Matches the visual
   language of the "Add ..." primary buttons (e.g. Add expense / Add income):
   solid accent fill with a white glyph + drop shadow, hover-opacity reduction.
   The accent fill makes the affordance unmistakable at rest in every theme. */
.sidebar__toggle {
  position: absolute;
  top: 1.5rem;
  /* Straddles the sidebar's right edge — half inside, half over the seam.
     Reads as a tab handle attached to the sidebar, not a floating button. */
  right: -13px;
  z-index: 30;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  border: none;
  color: var(--surface-primary);
  background: var(--color-primary);
  box-shadow:
    0 1px 2px color-mix(in srgb, var(--color-darkest), transparent 88%),
    0 2px 10px color-mix(in srgb, var(--color-darkest), transparent 82%);
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.sidebar__toggle:hover {
  opacity: 0.9;
  transform: scale(1.06);
  box-shadow:
    0 2px 4px color-mix(in srgb, var(--color-darkest), transparent 84%),
    0 4px 14px color-mix(in srgb, var(--color-darkest), transparent 74%);
}

.sidebar__toggle:active {
  transform: scale(0.96);
}

.sidebar__toggle:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.sidebar__toggle-icon {
  width: 14px;
  height: 14px;
  color: inherit;
  /* Force stroke via CSS (rather than relying on currentColor inheritance
     through the SVG's stroke attribute) so the chevron is guaranteed to
     paint in every browser. */
  stroke: currentColor;
}

.sidebar__toggle-icon path {
  stroke: currentColor;
  stroke-width: 3;
}

.sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  /* The 4px top gutter gives the active item's translateY(-2px) lift and its
     outward drop shadow room to paint. overflow-y:auto below clips both axes,
     so without the gutter the topmost item's highlight is shaved off at the
     seam with the workspace selector directly above. */
  padding: 4px 0.75rem 0;
  /* Take the free vertical space and scroll internally so the Settings footer
     (pinned via margin-top:auto) stays visible on short viewports — notably
     iPad landscape (1024x768), which renders the desktop sidebar but is only
     768px tall. Without this the nav overflows and the shell's overflow:hidden
     clips the footer. Mirrors .mobile-sidebar__nav. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* Quiet text on the sidebar (the locked Campaigns lock icon, the build
   version). In light mode most sidebars are a dark gradient, where --text-muted
   (a mid gray chosen for the page) turns faint, so light mode uses the sidebar's
   own text color. Dark mode keeps --text-muted exactly.
   No light-dark() (review ui IN-10): browsers without it (Safari before 17.5,
   Chrome before 123) would drop the color in dark mode too. The app picks a
   dark theme exactly when prefers-color-scheme is dark (App.js
   applyThemeVariant), so the same media query as the boot palette above
   selects the dark values, from the first frame. */
:root {
  --sidebar-text-quiet: var(--sidebar-text-color, var(--text-muted));
  /* The plain build line (no What's New link) was --text-faint before D-27, so
     dark mode keeps --text-faint for it exactly. */
  --sidebar-text-quiet-faint: var(--sidebar-text-color, var(--text-faint));
  /* Phase 70 (D-03): width of the docked KeyFlow AI panel on desktop. 0px
     while closed; KeyFlowAiSurfaces sets it to the dock width when it opens,
     and the app shell pads <main> by it so the page sits beside the dock. */
  --ai-dock-width: 0px;
}
@media (prefers-color-scheme: dark) {
  :root {
    --sidebar-text-quiet: var(--text-muted);
    --sidebar-text-quiet-faint: var(--text-faint);
  }
}

.sidebar__item {
  width: 100%;
  display: flex;
  align-items: center;
  /* Left-aligned. The Campaigns entry is a Button primitive, whose base classes
     include justify-center; this sheet loads after tailwind.css, so this wins
     and it lines up with the plain <button> siblings. */
  justify-content: flex-start;
  gap: 0.75rem;
  /* Horizontal padding chosen so 24px icons land with center at 38px (sidebar
     center in the 76px collapsed state) — left padding 14px + nav padding 12px
     puts the icon-left at 26px, icon-center at 38px. Settings and other footer
     items share the same column. */
  padding: 0.65rem 0.875rem;
  border-radius: 0.75rem;
  color: inherit;
  cursor: pointer;
  background: transparent;
  /* No border — the active-state outline is rendered via inset box-shadow
     (see .sidebar__item--active below) so the icon's content area is the
     full padding-box width and doesn't get reduced by 2px of border. */
  text-align: left;
  font-size: 0.95rem;
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, opacity 0.2s ease, box-shadow 0.25s ease;
  opacity: 0.88;
}

/* Several nav SVG icons (notably Clients with its left-side binder holes)
   have visual content shifted ~0.5–1 px left within their 24-unit viewBox,
   so even with a perfectly symmetric highlight box the icon's visible glyph
   reads as left-of-center inside it. Nudge .sidebar__icon right by 1 px in
   collapsed state to compensate. Scoped to .sidebar__icon so this does not
   move the workspace avatar (.sidebar__workspace-avatar, already perfect)
   or the floating toggle (.sidebar__toggle-icon). */
.sidebar--collapsed .sidebar__icon {
  transform: translateX(1px);
}

.sidebar__item:hover {
  background-color: var(--sidebar-item-hover);
  opacity: 1;
}

.sidebar__item--active {
  background-color: var(--sidebar-item-active);
  color: var(--sidebar-text-color, var(--text-primary));
  opacity: 1;
  /* The 1px ring is rendered as an inset box-shadow rather than a border so
     it doesn't push the icon over by 1px when toggling between states. */
  box-shadow:
    inset 0 0 0 1px var(--sidebar-active-border),
    var(--sidebar-active-shadow);
  transform: translateY(-2px);
}

.sidebar__item:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.45);
  outline-offset: 2px;
}

.sidebar__icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}

.sidebar__footer {
  margin-top: auto;
  /* Horizontal padding matches .sidebar__nav (0.75rem) so the Settings icon
     ends up in the same column as the nav icons (icon-center at 38px). */
  padding: 1.25rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid var(--sidebar-border-color, var(--border-color));
}

.sidebar__item--danger {
  color: var(--color-danger);
  opacity: 1;
}

.sidebar__item--danger:hover,
.sidebar__item--danger:focus-visible {
  background-color: var(--surface-danger);
  color: var(--color-danger);
}

.sidebar__item--danger .sidebar__icon {
  color: inherit;
}

.sidebar__item svg {
  color: inherit;
}


/* Child workspace context banner */
.sidebar__child-banner {
  margin: 0 0.75rem 0.5rem;
  padding: 0.625rem 0.75rem;
  background: var(--color-primary);
  color: #fff;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
}

.sidebar__child-banner--collapsed {
  /* Keep vertical padding identical to the expanded state to prevent
     the sidebar's vertical layout from shifting on collapse. Only the
     horizontal margin tightens. */
  margin: 0 0.5rem 0.5rem;
  align-items: center;
}

.sidebar__child-banner-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
  overflow: hidden;
}

.sidebar__child-banner-label span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar__child-banner-back {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 0.375rem;
  color: #fff;
  padding: 0.3rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.sidebar__child-banner-back:hover {
  background: rgba(255,255,255,0.35);
}


.mobile-sidebar-backdrop,
.mobile-sidebar,
.mobile-app-header {
  display: none;
}

a,
button,
label {
  color: inherit;
}

.bg-white,
.bg-gray-50 {
  background-color: var(--surface-primary) !important;
  color: var(--text-primary) !important;
}

.bg-gray-100,
.bg-gray-200 {
  background-color: var(--surface-muted) !important;
  color: var(--text-primary) !important;
}

.bg-gray-300,
.bg-gray-400 {
  background-color: var(--surface-strong) !important;
  color: var(--text-primary) !important;
}

.bg-blue-50,
.bg-blue-100,
.bg-indigo-50,
.bg-indigo-100,
.bg-purple-50 {
  background-color: var(--surface-info) !important;
  color: var(--color-accent) !important;
}

.bg-green-50,
.bg-green-100 {
  background-color: var(--surface-success) !important;
  color: var(--color-secondary) !important;
}

.bg-green-500\/10,
.dark\:bg-green-500\/10 {
  background-color: var(--surface-success) !important;
  color: var(--color-secondary) !important;
}

.bg-red-50,
.bg-red-100,
.bg-rose-50 {
  background-color: var(--surface-danger) !important;
  color: var(--color-danger) !important;
}

.bg-yellow-50,
.bg-amber-50,
.bg-orange-50 {
  background-color: var(--surface-warning) !important;
  color: var(--color-dark) !important;
}

.bg-blue-400,
.bg-blue-500,
.bg-blue-600,
.bg-blue-700,
.bg-indigo-500,
.bg-indigo-600,
.bg-indigo-700 {
  background-color: var(--color-primary) !important;
  color: #ffffff !important;
}

.bg-green-500,
.bg-green-600,
.bg-green-700 {
  background-color: var(--color-secondary) !important;
  color: #ffffff !important;
}

.bg-red-500,
.bg-red-600,
.bg-red-700 {
  background-color: var(--color-danger) !important;
  color: #ffffff !important;
}

.bg-slate-800,
.bg-slate-900,
.dark\:bg-slate-900,
.dark\:bg-slate-800,
.dark\:bg-slate-700,
.dark\:bg-slate-950\/60 {
  background-color: var(--surface-subtle) !important;
  color: var(--text-primary) !important;
}

.hover\:bg-white:hover {
  background-color: var(--surface-primary) !important;
  color: var(--text-primary) !important;
}

.hover\:bg-gray-50:hover,
.hover\:bg-gray-100:hover,
.hover\:bg-gray-200:hover,
.hover\:bg-gray-300:hover {
  background-color: var(--surface-hover) !important;
  color: var(--text-primary) !important;
}

.hover\:bg-blue-50:hover,
.hover\:bg-blue-100:hover,
.hover\:bg-indigo-50:hover {
  background-color: var(--surface-info) !important;
  color: var(--color-accent) !important;
}

.hover\:bg-blue-600:hover,
.hover\:bg-blue-700:hover,
.hover\:bg-indigo-600:hover,
.hover\:bg-indigo-700:hover {
  background-color: var(--color-secondary) !important;
  color: #ffffff !important;
}

.hover\:bg-green-600:hover,
.hover\:bg-green-700:hover {
  background-color: var(--color-primary) !important;
  color: #ffffff !important;
}

.hover\:bg-red-100:hover,
.hover\:bg-red-50:hover {
  background-color: var(--surface-danger) !important;
  color: var(--color-danger) !important;
}

.hover\:bg-red-600:hover,
.hover\:bg-red-700:hover {
  background-color: var(--color-danger) !important;
  color: #ffffff !important;
}

.text-gray-900,
.text-gray-800,
.text-gray-700,
.dark\:text-slate-100,
.dark\:text-slate-200,
.dark\:text-slate-300 {
  color: var(--text-primary) !important;
}

.text-gray-600,
.text-gray-500,
.text-gray-400,
.dark\:text-slate-400,
.dark\:text-slate-500 {
  color: var(--text-muted) !important;
}

.text-gray-300,
.text-gray-200,
.dark\:text-slate-600 {
  color: var(--border-color) !important;
}

.text-blue-400,
.text-blue-500,
.text-blue-600,
.text-blue-700,
.text-blue-800,
.text-blue-900,
.text-indigo-500,
.text-indigo-600,
.text-indigo-700,
.text-indigo-800,
.text-indigo-900,
.dark\:text-blue-300 {
  color: var(--action-link-color) !important;
}

.hover\:text-blue-600:hover,
.hover\:text-blue-700:hover,
.hover\:text-blue-800:hover,
.hover\:text-blue-900:hover,
.hover\:text-indigo-600:hover,
.hover\:text-indigo-700:hover,
.hover\:text-indigo-800:hover,
.hover\:text-indigo-900:hover {
  color: var(--action-link-color) !important;
}

.text-green-200,
.text-green-300,
.text-green-400,
.text-green-500,
.text-green-600,
.text-green-700,
.text-green-800,
.text-green-900,
.dark\:text-green-200 {
  color: var(--color-secondary) !important;
}

.text-red-500,
.text-red-600,
.text-red-700,
.text-red-800,
.text-red-900 {
  color: var(--color-danger) !important;
}

.border,
.border-gray-100,
.border-gray-200,
.border-gray-300,
.dark\:border-slate-600,
.dark\:border-slate-700,
.dark\:border-slate-800 {
  border-color: var(--border-color) !important;
}

.border-blue-100,
.border-blue-200,
.border-blue-300 {
  border-color: var(--color-accent) !important;
}

.border-green-100,
.border-green-200,
.border-green-300,
.border-green-400,
.dark\:border-green-700\/40 {
  border-color: var(--color-secondary) !important;
}

.border-red-100,
.border-red-200,
.border-red-300,
.border-red-400 {
  border-color: var(--color-danger) !important;
}

input,
textarea,
select {
  font-family: inherit;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  border-color: var(--border-color);
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

input:disabled,
textarea:disabled,
select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

input::placeholder,
textarea::placeholder {
  color: var(--text-muted);
  opacity: 1;
}

input:focus,
textarea:focus,
select:focus {
  border-color: var(--color-accent);
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}

select option {
  background-color: var(--surface-primary);
  color: var(--text-primary);
}

.shadow-sm {
  box-shadow: 0 1px 2px rgba(11, 31, 24, 0.08);
}

.shadow-md {
  box-shadow: 0 10px 15px -3px rgba(11, 31, 24, 0.08), 0 4px 6px -4px rgba(11, 31, 24, 0.06);
}

.transactions-scroll {
  overscroll-behavior: contain;
}

@media (max-width: 1023px) {
  /* Mobile keeps the same fixed-viewport, inner-scroll model as desktop: the
     shell is a definite 100dvh box (see App.js), its <main> owns the scroll via
     overflow-y-auto. We must NOT loosen html/body/#root to height:auto or
     body overflow:auto here — doing so removes the definite-height chain the
     inner scroll pane resolves against, which silently kills scrolling on
     every page. The gradient below is purely cosmetic. */
  .app-shell--mobile {
    background:
      radial-gradient(circle at top left, color-mix(in srgb, var(--color-primary), transparent 84%), transparent 28rem),
      var(--body-bg);
  }

  .app-shell--mobile > .sidebar:not(.mobile-sidebar) {
    display: none;
  }

  .mobile-app-header {
    display: block;
    position: sticky;
    top: 0;
    z-index: 40;
    padding:
      calc(0.75rem + env(safe-area-inset-top, 0px))
      1rem
      0.85rem;
    background: color-mix(in srgb, var(--surface-primary) 84%, transparent);
    border-bottom: 1px solid var(--border-color);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
    backdrop-filter: blur(18px) saturate(140%);
  }

  .mobile-app-header__top {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  /* The action slot renders on every page; with nothing portaled into it, the
     row's gap would still take 14px of title width. A callback ref still
     reaches a display:none node, so pages can fill it as before. */
  .mobile-app-header__actions:empty {
    display: none;
  }

  .mobile-app-header__menu {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 13px;
    border: 1px solid var(--border-color);
    background: var(--surface-primary);
    color: var(--text-primary);
    box-shadow: 0 12px 24px rgba(11, 31, 24, 0.08);
  }

  .mobile-app-header__title-group {
    min-width: 0;
    flex: 1;
  }

  .mobile-app-header__eyebrow {
    display: block;
    font-size: 0.72rem;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-muted);
    margin-bottom: 0.3rem;
  }

  .mobile-app-header__title {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.2;
    color: var(--text-primary);
  }

  .mobile-sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 49;
    background: rgba(11, 31, 24, 0.38);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  .mobile-sidebar-backdrop--open {
    opacity: 1;
    pointer-events: auto;
  }

  .mobile-sidebar {
    display: flex;
    flex-direction: column;
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 50;
    width: min(20rem, calc(100vw - 2rem));
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    border-right: 1px solid var(--sidebar-border-color, var(--border-color));
    box-shadow: 0 24px 48px rgba(11, 31, 24, 0.28);
  }

  .mobile-sidebar--open {
    transform: translateX(0);
  }

  .mobile-sidebar__top {
    padding-top: calc(1rem + env(safe-area-inset-top, 0px));
  }

  .mobile-sidebar__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 13px;
    border: 1px solid var(--sidebar-border-color);
    color: inherit;
    background: var(--sidebar-item-hover);
  }

  .mobile-sidebar__nav {
    flex: 1;
    min-height: 0;
    padding-bottom: 1rem;
    overflow-y: auto;
  }

  .mobile-sidebar__footer {
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }
}

.email-preview-editor__content {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.email-preview-editor__content a {
  word-break: break-all;
}

.email-preview-editor__content img,
.email-preview-editor__content video,
.email-preview-editor__content iframe {
  max-width: 100%;
  height: auto;
}

.email-preview-editor__content table {
  width: 100% !important;
  max-width: 100%;
  table-layout: fixed;
}

.email-preview-editor__content th,
.email-preview-editor__content td {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* ============================================================
   Income v2 — utility classes ported from mockup
   ============================================================ */

/* Flat at rest, per DESIGN.md's Flat-At-Rest Rule: a hairline border holds the
   edge identically in all 13 themes, where a shadow cannot. This class used to
   carry a hardcoded rgba(11,31,24,...) shadow and no border, which left every
   .card edgeless on the five dark themes (#161614 on #0B0B0A). Matches the
   `Card` primitive in src/components/ui/primitives/Card.js. If a surface has
   genuinely lifted, apply var(--shadow-card) at the call site. */
.card {
  background: var(--surface-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.card-subtle {
  background: var(--surface-primary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
}

.kpi-num {
  font-family: var(--font-sans);
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.mono {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  background: var(--surface-info);
  color: var(--color-primary);
  border: 1px solid rgba(20, 81, 61, 0.18);
}

.pill-warn {
  background: var(--surface-warning);
  color: var(--pill-warn-text);
  border-color: rgba(196,154,76,0.4);
}

.pill-muted {
  background: var(--surface-subtle);
  color: var(--text-muted);
  border-color: var(--border-color);
}

.pill-good { background: var(--surface-success); color: var(--color-primary); border-color: rgba(20,81,61,0.2); }

.meter {
  position: relative;
  height: 8px;
  border-radius: 999px;
  background: var(--surface-subtle);
  overflow: hidden;
}

.meter .fill {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: linear-gradient(90deg, var(--color-primary), color-mix(in srgb, var(--color-primary), white 14%));
  border-radius: 999px;
}

.meter .marker {
  position: absolute;
  top: -3px;
  bottom: -3px;
  width: 2px;
  background: var(--text-primary);
  border-radius: 9999px;
}

.bar { background: var(--color-primary); border-radius: 2px; width: 100%; }
.bar-warn { background: var(--color-warning); border-radius: 2px; width: 100%; opacity: 0.85; }
.bar-muted { background: var(--surface-strong); border-radius: 2px; width: 100%; opacity: 0.55; }
.divider { height: 1px; background: var(--border-color); width: 100%; }

/* Segmented filter control */
.seg { display: inline-flex; border: 1px solid var(--border-color); border-radius: 6px; overflow: hidden; }
.seg button { padding: 8px 14px; font-size: 11px; background: transparent; color: var(--text-muted); border: none; cursor: pointer; white-space: nowrap; min-height: 40px; }
.seg button.active { background: var(--surface-subtle); color: var(--text-primary); }

/* Shared "on" language (Phase 56, D-33, UI-SPEC B-2). Toggle chips and any
   element that is "on" but not a toggle (.kf-toggle-chip--on) read as selected
   by fill, weight, and a 2px border, never by color alone. Doubled class names
   beat the Button primitive's utility classes. Tokens only. */
.kf-toggle-chip.kf-toggle-chip {
    background: transparent;
    color: var(--text-primary);
    font-weight: 400;
    border: 1px solid var(--text-muted);
    border-radius: 8px;
    padding-inline: 12px;
}
.kf-toggle-chip.kf-toggle-chip--pill { border-radius: 9999px; }
@media (hover: hover) {
    .kf-toggle-chip.kf-toggle-chip:hover:not(:disabled):not([aria-pressed="true"]):not(.kf-toggle-chip--on) {
        background: var(--surface-hover);
        border-color: var(--text-primary);
    }
}
.kf-toggle-chip.kf-toggle-chip:active:not(:disabled):not([aria-pressed="true"]):not(.kf-toggle-chip--on) {
    background: var(--surface-strong);
}
.kf-toggle-chip.kf-toggle-chip[aria-pressed="true"],
.kf-toggle-chip.kf-toggle-chip.kf-toggle-chip--on {
    background: var(--text-primary);
    color: var(--surface-primary);
    font-weight: 600;
    border: 2px solid var(--text-primary);
    padding-inline: 11px;
}
@media (hover: hover) {
    .kf-toggle-chip.kf-toggle-chip[aria-pressed="true"]:hover:not(:disabled),
    .kf-toggle-chip.kf-toggle-chip.kf-toggle-chip--on:hover:not(:disabled) {
        opacity: 0.9;
    }
}
.kf-toggle-chip.kf-toggle-chip:focus-visible {
    outline: 2px solid var(--text-primary);
    outline-offset: 2px;
    box-shadow: none;
}
.kf-toggle-chip.kf-toggle-chip:disabled,
.kf-toggle-chip.kf-toggle-chip[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
}
@media (forced-colors: active) {
    .kf-toggle-chip.kf-toggle-chip[aria-pressed="true"],
    .kf-toggle-chip.kf-toggle-chip.kf-toggle-chip--on {
        border: 2px solid Highlight;
    }
}

/* Closings table row grid — desktop only. The mobile card layout in
   ClosingsBreakdownTable.js renders a different markup at < 768px and
   relies on Tailwind's `hidden` class to suppress the desktop grid row.
   Wrapping these rules in the media query prevents `display: grid` from
   overriding `display: none` at mobile widths. */
@media (min-width: 768px) {
    .deal-row { display: grid; grid-template-columns: minmax(0, 1fr) 9rem 7rem 7rem; gap: 14px; padding: 12px 0; align-items: center; }
    .deal-row:last-child { border-bottom: none !important; }
    .deal-row:hover { background: var(--surface-muted); border-radius: 4px; }
}

/* Stage badges */
.stage { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.04em; padding: 3px 7px; border-radius: 4px; text-transform: uppercase; }
.stage-closed { background: var(--color-primary); color: var(--on-primary); }
.stage-contract { background: var(--surface-warning); color: var(--pill-warn-text); border: 1px solid rgba(196,154,76,0.4); }
.divider { height: 1px; background: var(--border-color); width: 100%; }

/* Promoted from sketch theme (Goal #3) */

/* Typography */
.kf-page { max-width: 1400px; margin: 0 auto; padding: 28px 32px 30vh; }
.kf-h1 { font-size: 30px; font-weight: 700; letter-spacing: -0.03em; line-height: 1.2; color: var(--text-primary); }
.kf-sub { font-size: 14px; color: var(--text-muted); margin-top: 4px; line-height: 1.5; }
.kf-eyebrow { font-family: var(--font-sans); font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--text-muted); }
.kf-h2 { font-size: 18px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
.kf-h3 { font-size: 13px; font-weight: 600; color: var(--text-muted); }

/* Global anchor focus ring (D-16). Deliberately global rather than
   per-component: every link inside .help-article prose below is
   build-generated HTML with no component to edit, so a per-component fix
   reaches none of the ~30 articles that carry prose links — and this same
   rule then also covers HelpToc's two link sites, HelpSearch's result rows
   and its two inline recovery links, HelpMenuPanel's row, the breadcrumb,
   and related links, so no surface needs its own treatment.
   --color-accent (not --color-primary) is the token every other focus ring
   in the app already uses (Button, IconButton, Input, Select, Dropdown). */
a:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Help-article prose block — themes the prerendered fragment HTML rendered
   via dangerouslySetInnerHTML (src/components/features/help/HelpArticlePage.js).
   That HTML carries exactly one class, `shot`, so component-scoped Tailwind
   utility classes cannot reach it; this scoped block is the only way to theme
   it across all 13 themes. scroll-margin-top on h2 is load-bearing: 56px
   sticky header + 32px breathing room, so a TOC anchor jump never lands a
   heading under the chrome. */
.help-article { font-size: 1rem; line-height: 1.5; color: var(--text-primary); }
.help-article p { margin: 0 0 16px; }
.help-article p:last-child { margin-bottom: 0; }
.help-article h2 {
  font-size: 18px; font-weight: 600; letter-spacing: -0.02em;
  color: var(--text-primary); margin: 28px 0 12px; scroll-margin-top: 88px;
}
.help-article figure.shot { margin: 28px 0; }
.help-article figure.shot img {
  display: block; width: 100%; height: auto;
  border: 1px solid var(--border-color); border-radius: 10px;
}
.help-article figure.shot figcaption {
  font-family: var(--font-mono); font-size: 10.5px;
  letter-spacing: 0.04em; color: var(--text-muted); margin-top: 10px;
}

/* D-06 tap-to-zoom affordance (47-06). The trigger/dialog markup is
   build-generated by scripts/lib/renderArticleHtml.js's
   transformShotFigures() -- see that file's header comment for the exact
   emitted shape. The figure-scoped rules below are scoped to .help-article
   like the figure rules above; the dialog and its ::backdrop are NOT
   scoped, because a <dialog> opened via showModal() renders in the top
   layer and is no longer a descendant of .help-article while open. */
.help-article figure.shot .shot-zoom-trigger {
  display: block; position: relative; width: 100%; padding: 0; margin: 0;
  border: 0; background: none; cursor: zoom-in;
}
.help-article figure.shot .shot-zoom-chip {
  position: absolute; right: 8px; bottom: 8px;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 8px; border-radius: 8px;
  font-size: 12px; font-weight: 600; line-height: 1.2;
  background: color-mix(in srgb, var(--overlay-strong-bg) 82%, transparent);
  color: var(--overlay-strong-text);
  pointer-events: none;
}
dialog.help-zoom-dialog {
  position: relative;
  width: 100vw; height: 100dvh; max-width: 100vw; max-height: 100dvh;
  margin: 0; padding: 16px; border: 0; border-radius: 0;
  background: var(--surface-primary);
}
@media (min-width: 640px) {
  dialog.help-zoom-dialog {
    width: auto; height: auto;
    max-width: min(1280px, 96vw); max-height: 92vh;
    margin: auto; padding: 16px; border-radius: 12px;
    border: 1px solid var(--border-color); box-shadow: var(--shadow-lift);
  }
}
/* ::backdrop inheriting custom properties from its originating element is
   baseline in current engines. An engine that predates that renders the
   backdrop untinted over an already-opaque dialog -- graceful degradation,
   not a broken affordance. No rgba fallback is added: that would put a
   colour literal in this file. */
dialog.help-zoom-dialog::backdrop {
  background: color-mix(in srgb, var(--overlay-strong-bg) 70%, transparent);
}
dialog.help-zoom-dialog .help-zoom-close {
  position: absolute; top: 16px; right: 16px;
  min-width: 44px; min-height: 44px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--border-color); border-radius: 8px;
  background: var(--surface-primary); color: var(--text-primary);
  cursor: pointer;
}
/* This container scrolls horizontally on purpose. ACCESS-04 forbids THE
   PAGE scrolling horizontally; a pan inside a zoom view the reader opened
   deliberately is not that (47-CONTEXT.md D-06). */
dialog.help-zoom-dialog .help-zoom-scroll {
  overflow: auto; -webkit-overflow-scrolling: touch;
  max-width: 100%; max-height: 100%;
}
/* max-width: none is load-bearing -- it defeats the width:100% on the
   inline figure image that would otherwise shrink the zoomed copy back
   down. The emitted <img> carries its own width/height attributes, so
   width:auto renders it at intrinsic size with no hardcoded 1280px here. */
dialog.help-zoom-dialog .help-zoom-scroll img {
  display: block; width: auto; max-width: none; height: auto;
  border: 0; border-radius: 0;
}
.shot-zoom-trigger:focus-visible,
.help-zoom-close:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Toggle switch */
.toggle { position: relative; display: inline-block; vertical-align: middle; width: 44px; height: 26px; flex-shrink: 0; cursor: pointer; }
.toggle input { position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
.toggle .track {
  position: absolute; inset: 0; border-radius: 9999px; background: var(--surface-strong);
  transition: background .2s var(--ease-rise);
}
.toggle .thumb {
  position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 9999px;
  /* Deliberately literal white, not --surface-primary: the thumb must read as
     "on" against both the neutral track and the mint checked track, in light
     AND dark themes. --surface-primary is #161614 on dark, which would make
     the thumb vanish. */
  background: #fff; box-shadow: 0 1px 3px rgba(11, 31, 24, 0.25); transition: transform .2s var(--ease-rise);
}
.toggle .thumb { display: flex; align-items: center; justify-content: center; color: var(--text-muted); }
.toggle input:checked ~ .track { background: var(--financial-action-color); }
.toggle input:checked ~ .thumb { transform: translateX(18px); }
/* The input is the real control but is invisible (opacity 0), so its outline
   would be too. Draw the keyboard focus ring on the track instead, with the
   same --color-accent ring every other control uses (WCAG 2.4.7). */
.toggle input:focus-visible ~ .track { outline: 2px solid var(--color-accent); outline-offset: 2px; }

/* Avatar */
.avatar {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 44px; height: 44px; border-radius: 13px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-primary), transparent 78%), color-mix(in srgb, var(--color-secondary), transparent 80%));
  color: var(--color-primary); font-family: var(--font-sans); font-weight: 600; font-size: 16px;
}
.avatar--square { border-radius: 10px; }

/* Misc */
.muted { color: var(--text-muted); }
.badge .dot { width: 6px; height: 6px; border-radius: 9999px; background: currentColor; }
.row-divide > * + * { border-top: 1px solid var(--border-color); }
.icon-chip {
  display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px;
  border-radius: 10px; flex-shrink: 0;
}
.toast { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 500; color: var(--financial-action-color); }

/* Page-load rise animation */
@keyframes kf-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.rise { opacity: 0; animation: kf-rise .5s var(--ease-rise) forwards; }
/* Cross-tab playbook FAB entrance — pops up from the corner when a tab that has
   it becomes active. No fill-forwards, so the resting state stays natural and
   the hover scale transition keeps working after the entrance. */
@keyframes kf-fab-in { from { opacity: 0; transform: translateY(12px) scale(.85); } to { opacity: 1; transform: none; } }
/* Exit is the exact reverse of kf-fab-in; forwards holds the hidden end state
   until the FAB unmounts. */
@keyframes kf-fab-out { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(12px) scale(.85); } }
